/**
 * Cloudflare Worker API クライアント
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

const BASE_URL = "https://image-processor.el-calafate.workers.dev";

type Json = Record<string, unknown>;

async function request<T = unknown>(
  path: string,
  init?: RequestInit,
  errorMessage = "API request failed"
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  let res: Response;
  if (!init) {
    res = await fetch(url);
  } else if (init.body !== undefined) {
    res = await fetch(url, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init.headers || {}),
      },
    });
  } else {
    res = await fetch(url, init);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${errorMessage} (${res.status}): ${body}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // ---------- illustrations ----------
  getIllustrations: () => request<any[]>("/api/illustrations", undefined, "Failed to fetch illustrations"),

  getIllustration: (id: string) =>
    request<any>(`/api/illustrations/${id}`, undefined, "Failed to fetch illustration"),

  createIllustration: (data: Json) =>
    request<any>(
      "/api/illustrations",
      { method: "POST", body: JSON.stringify(data) },
      "Failed to create illustration"
    ),

  updateIllustration: (id: string, data: Json) =>
    request<any>(
      `/api/illustrations/${id}`,
      { method: "PUT", body: JSON.stringify(data) },
      "Failed to update illustration"
    ),

  deleteIllustration: (id: string) =>
    request<any>(`/api/illustrations/${id}`, { method: "DELETE" }, "Failed to delete illustration"),

  // ---------- tags ----------
  getTags: () => request<Array<{ id: number; tag: string }>>("/api/tags", undefined, "Failed to fetch tags"),

  createTag: (tag: string) =>
    request<{ id: number; tag: string }>(
      "/api/tags",
      { method: "POST", body: JSON.stringify({ tag }) },
      "Failed to create tag"
    ),

  deleteTag: (id: number) =>
    request<any>(`/api/tags/${id}`, { method: "DELETE" }, "Failed to delete tag"),

  // ---------- works ----------
  getWorks: (all = false) =>
    request<any[]>(`/api/works${all ? "?all=1" : ""}`, undefined, "Failed to fetch works"),

  getWorksIllustrations: (worksId: number) =>
    request<any[]>(`/api/works/${worksId}/illustrations`, undefined, "Failed to fetch works illustrations"),

  createWorks: (data: Json) =>
    request<any>(
      "/api/works",
      { method: "POST", body: JSON.stringify(data) },
      "Failed to create works"
    ),

  updateWorks: (id: number, data: Json) =>
    request<any>(
      `/api/works/${id}`,
      { method: "PUT", body: JSON.stringify(data) },
      "Failed to update works"
    ),

  deleteWorks: (id: number) =>
    request<any>(`/api/works/${id}`, { method: "DELETE" }, "Failed to delete works"),

  // ---------- works-placements ----------
  getWorksPlacements: (worksId?: number) => {
    const path = worksId ? `/api/works-placements?works_id=${worksId}` : `/api/works-placements`;
    return request<any[]>(path, undefined, "Failed to fetch works placements");
  },

  createWorksPlacement: (data: Json) =>
    request<any>(
      "/api/works-placements",
      { method: "POST", body: JSON.stringify(data) },
      "Failed to create works placement"
    ),

  updateWorksPlacement: (id: string, data: Json) =>
    request<any>(
      `/api/works-placements/${id}`,
      { method: "PUT", body: JSON.stringify(data) },
      "Failed to update works placement"
    ),

  deleteWorksPlacement: (id: string) =>
    request<any>(`/api/works-placements/${id}`, { method: "DELETE" }, "Failed to delete works placement"),

  // ---------- layout-sections ----------
  getLayoutSections: () =>
    request<any[]>("/api/layout-sections", undefined, "Failed to fetch layout sections"),

  createLayoutSection: (data: Json) =>
    request<any>(
      "/api/layout-sections",
      { method: "POST", body: JSON.stringify(data) },
      "Failed to create layout section"
    ),

  updateLayoutSection: (id: number, data: Json) =>
    request<any>(
      `/api/layout-sections/${id}`,
      { method: "PUT", body: JSON.stringify(data) },
      "Failed to update layout section"
    ),

  deleteLayoutSection: (id: number) =>
    request<any>(`/api/layout-sections/${id}`, { method: "DELETE" }, "Failed to delete layout section"),

  // ---------- image-placements ----------
  getPlacements: (sectionId: number) =>
    request<any[]>(`/api/placements/${sectionId}`, undefined, "Failed to fetch placements"),

  getImagePlacements: (sectionId?: number) => {
    const path = sectionId
      ? `/api/image-placements?section_id=${sectionId}`
      : `/api/image-placements`;
    return request<any[]>(path, undefined, "Failed to fetch image placements");
  },

  createImagePlacement: (data: Json) =>
    request<any>(
      "/api/image-placements",
      { method: "POST", body: JSON.stringify(data) },
      "Failed to create image placement"
    ),

  updateImagePlacement: (id: number, data: Json) =>
    request<any>(
      `/api/image-placements/${id}`,
      { method: "PUT", body: JSON.stringify(data) },
      "Failed to update image placement"
    ),

  deleteImagePlacement: (id: number) =>
    request<any>(`/api/image-placements/${id}`, { method: "DELETE" }, "Failed to delete image placement"),

  // ---------- content-texts ----------
  getContentText: (section: string, key: string) =>
    request<any>(
      `/api/content-texts?section=${encodeURIComponent(section)}&key=${encodeURIComponent(key)}`,
      undefined,
      "Failed to fetch content text"
    ),

  upsertContentText: (section: string, key: string, content: string) =>
    request<any>(
      "/api/content-texts",
      { method: "PUT", body: JSON.stringify({ section, key, content }) },
      "Failed to save content text"
    ),

  // ---------- upload ----------
  uploadImage: async (file: File, category: string, fileName: string) => {
    const res = await fetch(
      `${BASE_URL}/api/upload?filename=${encodeURIComponent(fileName)}&category=${encodeURIComponent(category)}`,
      {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      }
    );
    if (!res.ok) throw new Error(`Upload failed: ${await res.text()}`);
    return res.json();
  },

  // ---------- image proxy ----------
  getImageUrl: (path: string, options: { width?: number; watermark?: boolean } = {}) => {
    const url = new URL(BASE_URL);
    url.searchParams.set("path", path);
    if (options.width) url.searchParams.set("width", options.width.toString());
    if (options.watermark) url.searchParams.set("watermark", "true");
    return url.toString();
  },
};

export const IMAGE_PROCESSOR_URL = BASE_URL;
